import type { CSSProperties } from 'react';
import { z } from 'zod';
import { MainTitle } from '@/components/TextBlock';
import { widgetLayoutSchema } from '@/config/schema';
import { useWidgetData } from '@/hooks/useWidgetData';
import { useWidgetLayout } from '@/hooks/useWidgetLayout';
import { assetUrl, IMG } from '@/lib/assets';
import { configuredOr } from '@/lib/configValue';
import { fontOptionsSchema, fontStyleToCss, toCssFontSize } from '@/lib/fontStyle';
import { sanitizeHtml } from '@/lib/html';
import { warnOnce } from '@/lib/warnOnce';
import type { WidgetProps } from '@/widgets/registry';

/**
 * A ranked table of spaces — a header row of labels, then one row per space. Port of
 * `topSpacesWidget/topSpacesWidget.vue`.
 *
 * Used by four deployed configs (`corio`, `freshwaves`, `hbr_tcc_2`, `hbr_tcc_3`), all four
 * with the same four columns: space name, people count, enviro comfort, CO2 / humidity.
 *
 * Like `FloorOccupancyWidget` it draws no chart; it is in this ticket for its shared header.
 *
 * ## Styling
 *
 * The legacy component put its whole layout in a scoped SCSS block driven by fifteen Vue
 * `v-bind()` calls — every colour, radius, gap and font came from config through a generated
 * CSS custom property. There is no React equivalent and no need for one: the same values are
 * inline styles here, and the parts that are *not* config (`flex`, `text-center`, the row
 * padding) are Tailwind utilities. The legacy class names are kept as markers so a DOM diff
 * against the Vue app during SYM-506 lines up.
 *
 * The `-webkit-`/`-moz-` `border-radius` duplicates are dropped — every target browser is
 * evergreen Chromium.
 */

const datasetFontSchema = fontOptionsSchema;

const headerLabelSchema = z.object({
  /** Rendered as HTML — `CO<sub>2</sub>` in every config. Sanitised, see W-10 below. */
  name: z.string().default(''),
  /** A file under `src/assets/img/icons/`. Empty means no icon. */
  icon: z.string().default(''),
});

const optionsSchema = z.object({
  topic: z.string().optional(),
  layout: widgetLayoutSchema.optional(),
  mainTitle: z.string().optional(),
  mainTitleFont: fontOptionsSchema.optional(),
  dataset: z
    .object({
      /** rem, between rows. */
      gap: z.number().optional(),
      header: z
        .object({
          background: z.string().optional(),
          /** px [sic] — the only radius in the config vocabulary that is not rem. */
          borderRadius: z.number().optional(),
          font: datasetFontSchema.optional(),
          labels: z.array(headerLabelSchema).prefault([]),
        })
        .optional(),
      items: z
        .object({
          background: z.string().optional(),
          /** px [sic]. */
          borderRadius: z.number().optional(),
          font: datasetFontSchema.optional(),
          /** The single row rendered when the feed has nothing to show. */
          notAvailableText: z.string().optional(),
        })
        .optional(),
    })
    .prefault({}),
});

/** docs/MIGRATION-WIDGETS.md §7. Every cell is a string, and every string is broker-sourced. */
const payloadSchema = z.looseObject({
  rooms: z
    .array(z.looseObject({ color: z.string().optional(), columns: z.array(z.string()).prefault([]) }))
    .optional(),
});

type Room = NonNullable<z.infer<typeof payloadSchema>['rooms']>[number];

/** Legacy defaults, per key, for the two font blocks and the four box properties. */
const DEFAULT_FONT_SIZE = '0.8rem';
const TRANSPARENT = 'transparent';
const NO_RADIUS = '0px';
const NO_GAP = '0rem';

export function TopSpacesWidget({ componentId, options }: WidgetProps) {
  const parsed = optionsSchema.safeParse(options);
  // Hooks run before any early return.
  const opts = parsed.success ? parsed.data : undefined;
  const data = useWidgetData<unknown>(componentId, opts?.topic);
  const { className, style } = useWidgetLayout(opts?.layout);

  if (!parsed.success) {
    warnOnce(
      `top-spaces-options:${componentId}`,
      '[signage] TopSpacesWidget options rejected; rendering nothing. %s',
      z.prettifyError(parsed.error),
    );
    return null;
  }

  const { dataset, mainTitle, mainTitleFont } = parsed.data;
  const { header, items } = dataset;

  const headerLabels = header?.labels ?? [];
  const hasHeader = headerLabels.length > 0;

  const headerFont = fontStyleToCss(header?.font, { fontSize: DEFAULT_FONT_SIZE });
  const itemsFont = fontStyleToCss(items?.font, { fontSize: DEFAULT_FONT_SIZE });

  const rows = readRows(data, componentId, items?.notAvailableText ?? '');

  const itemsStyle: CSSProperties = {
    gap: dataset.gap ? `${String(dataset.gap)}rem` : NO_GAP,
  };
  const headerStyle: CSSProperties = {
    background: configuredOr(header?.background, TRANSPARENT),
    borderRadius: header?.borderRadius ? `${String(header.borderRadius)}px` : NO_RADIUS,
  };
  const rowStyle: CSSProperties = {
    background: configuredOr(items?.background, TRANSPARENT),
    borderRadius: items?.borderRadius ? `${String(items.borderRadius)}px` : NO_RADIUS,
    // Ported by SYM-502 into `global.css`. Declared inline because the row is the only user.
    animation: 'fadeItems 1s',
  };
  /** The icon is sized to the item font size — legacy `width: v-bind('itemsFont.fontSize')`. */
  const iconStyle: CSSProperties = {
    width: toCssFontSize(items?.font?.fontSize) ?? DEFAULT_FONT_SIZE,
    height: 'auto',
    objectFit: 'cover',
  };

  return (
    <div className={className} style={style}>
      <div className="top-spaces-container w-full flex flex-col">
        <MainTitle text={mainTitle ?? ''} font={mainTitleFont} />
        <div className="top-spaces-items w-full flex flex-col" style={itemsStyle}>
          {hasHeader ? (
            <div className="header rounded w-full flex justify-around p-2" style={headerStyle}>
              {headerLabels.map((label, index) => (
                <div
                  key={index}
                  className="header-item flex-1 text-center"
                  style={headerFont}
                  // W-10: config-sourced here, but the same sanitiser runs on both paths —
                  // see the body cells below, which are not.
                  dangerouslySetInnerHTML={{ __html: sanitizeHtml(label.name) }}
                />
              ))}
            </div>
          ) : null}
          {rows.map((row, rowIndex) => (
            <div
              key={rowIndex}
              className="top-space-row rounded w-full flex justify-around p-2"
              style={rowStyle}
            >
              {row.columns.map((column, columnIndex) => (
                <div
                  key={columnIndex}
                  className="top-space-row-item flex-1 text-center"
                  style={itemsFont}
                >
                  <div className="column flex flex-row justify-center items-center gap-[0.3rem]">
                    {columnIndex === 0 && row.color ? (
                      <span
                        className="dot inline-block"
                        style={{
                          backgroundColor: row.color,
                          height: '0.9rem',
                          width: '0.9rem',
                          borderRadius: '50%',
                        }}
                      />
                    ) : null}
                    {/*
                     * **W-10.** These are MQTT-sourced strings going through `v-html` in the
                     * legacy app, which makes anything able to publish to this topic able to
                     * inject markup into a wall-mounted display. Sanitised
                     * (docs/MIGRATION-WIDGETS.md §4); the allowlist keeps the `<b>`/`<sub>`
                     * formatting these payloads legitimately carry.
                     *
                     * **QA-9.** Restating the family here is load-bearing and is not a
                     * tidy-up. `global.css` declares `* { font-family: HubhubSans-Medium }`,
                     * and a universal selector *matches* this span, so it beats the family
                     * inherited from `itemsFont` two levels up — where the configured value
                     * lives. `inherit` does not rescue it either: `.column` is matched by the
                     * same rule, so there is nothing left to inherit. Without this,
                     * `dataset.items.font.fontFamily` is silently dropped for the cell text,
                     * and all four configs carrying this widget set it to `Arial`. The legacy
                     * component solves it the same way, with an explicit
                     * `.column span { font-family }` rule (`topSpacesWidget.vue`).
                     * Measured in SYM-506's portrait re-run.
                     */}
                    <span
                      style={{ fontFamily: itemsFont.fontFamily }}
                      dangerouslySetInnerHTML={{ __html: sanitizeHtml(column) }}
                    />
                    <ColumnIcon icon={headerLabels[columnIndex]?.icon} style={iconStyle} />
                  </div>
                </div>
              ))}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

/**
 * The per-column icon, taken from the **header** label at the same index and repeated in every
 * body row — which is what the legacy template does (`showIcon(colIndex)` reads `headerItems`),
 * however odd it looks. `corio` and friends put `people.svg` on column 1 and `wind.svg` on
 * column 2, so every space row shows both.
 */
function ColumnIcon({ icon, style }: { icon: string | undefined; style: CSSProperties }) {
  if (!icon) return null;

  const url = assetUrl(`${IMG.icons}${icon}`);
  if (url === undefined) return null;

  return (
    <span>
      <img src={url} className="icon" style={style} alt="" />
    </span>
  );
}

/**
 * The rows to render: the payload's `rooms`, or the single `notAvailableText` row.
 *
 * docs/MIGRATION-WIDGETS.md §9 — the fallback row is the empty state, and the legacy code
 * falls back to it not only before the first message but also whenever a message arrives with
 * an empty or missing `rooms`, which is how a "no space currently meets the thresholds" result
 * is expressed. Note the fallback row has exactly **one** cell, so it spans as one column
 * regardless of how many header labels there are.
 */
function readRows(data: unknown, componentId: string, notAvailableText: string): Room[] {
  const fallback: Room[] = [{ columns: [notAvailableText] }];
  if (data === undefined) return fallback;

  const payload = payloadSchema.safeParse(data);
  if (!payload.success) {
    warnOnce(
      `top-spaces-payload:${componentId}`,
      '[signage] TopSpacesWidget payload rejected; showing the notAvailableText row. %s',
      z.prettifyError(payload.error),
    );
    return fallback;
  }

  const rooms = payload.data.rooms;
  if (!rooms || rooms.length === 0) return fallback;
  return rooms;
}
